/**
 * API Client Configuration and Initialization
 * Sets up the HTTP client with interceptors for auth, logging, and error handling
 */

import { HttpClient, RequestConfig, HttpResponse } from './http-client';
import { ApiError, isApiError } from './errors';

export class ApiClient {
  private static instance: ApiClient;
  private httpClient: HttpClient;

  private constructor(baseURL: string) {
    this.httpClient = new HttpClient({
      baseURL,
      timeout: 30000,
      maxRetries: 2,
      retryDelay: 1000,
      retryBackoffMultiplier: 2,
    });

    this.setupInterceptors();
  }

  /**
   * Get or create the singleton instance
   */
  static getInstance(baseURL: string = ''): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient(baseURL);
    }
    return ApiClient.instance;
  }

  /**
   * Setup request/response/error interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - add auth token, logging
    this.httpClient.addRequestInterceptor(async (config: RequestConfig) => {
      // Add auth headers if token exists
      const token = this.getAuthToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      // Add common headers
      config.headers = {
        'Content-Type': 'application/json',
        ...config.headers,
      };

      // Log request in development
      if (process.env['NODE_ENV'] === 'development') {
        console.debug(`[API] ${config.method || 'GET'} ${config}`);
      }

      return config;
    });

    // Response interceptor - logging
    this.httpClient.addResponseInterceptor(async (response: HttpResponse) => {
      if (process.env['NODE_ENV'] === 'development') {
        console.debug(`[API] Response ${response.status}`, response.data);
      }
      return response;
    });

    // Error interceptor - handle specific error cases
    this.httpClient.addErrorInterceptor(async (error: ApiError) => {
      // Handle authentication errors
      if (error.statusCode === 401) {
        this.handleUnauthorized();
      }

      // Log errors in development (except suppressed ones)
      if (process.env['NODE_ENV'] === 'development' && !error.isSuppressible) {
        console.error(`[API Error] ${error.type}:`, error.toJSON());
      }

      return error;
    });
  }

  /**
   * Get authentication token
   */
  private getAuthToken(): string | null {
    // This will be replaced with actual auth token retrieval
    // For now, we assume cookies are being used (included via credentials: 'include')
    return null;
  }

  /**
   * Handle unauthorized errors
   */
  private handleUnauthorized(): void {
    // Clear auth state and redirect to login
    if (typeof window !== 'undefined') {
      const redirectUrl = encodeURIComponent(window.location.href);
      const loginUrl = process.env.REACT_APP_AUTH_WEB_URL || '/login';
      window.location.href = `${loginUrl}/signin?redirect=${redirectUrl}`;
    }
  }

  /**
   * Public API methods
   */
  get<T = unknown>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.httpClient.get<T>(url, config);
  }

  post<T = unknown>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.httpClient.post<T>(url, body, config);
  }

  put<T = unknown>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.httpClient.put<T>(url, body, config);
  }

  patch<T = unknown>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.httpClient.patch<T>(url, body, config);
  }

  delete<T = unknown>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.httpClient.delete<T>(url, config);
  }
}

// Export the error types
export { ApiError, isApiError } from './errors';
export type { ApiErrorResponse, ApiErrorType } from './errors';
